/**
 * Kawałki mapy dociągane ZANIM człowiek na nią wejdzie.
 *
 * DLACZEGO. Mapa jest leniwa (MapView + maplibre-gl, ~1,3 MB). Karta otwarta przed wdrożeniem
 * przy pierwszym wejściu na mapę prosiła o plik z hashem sprzed wdrożenia, dostawała 404
 * i ErrorBoundary przeładowywał stronę — zmierzone 29.09 w symulacji wdrożenia (1 → 2
 * załadowania). To było „mapa czasem dziwnie się przeładowuje przy wchodzeniu”.
 *
 * KIEDY (decyzja orkiestratora 29.09 — nie płacimy 1,3 MB wszystkim):
 *  • przy ZAMIARZE: najechanie, fokus albo dotknięcie przycisku „Mapa”;
 *  • w tle TYLKO na szybkim łączu bez oszczędzania danych (`moznaDociagacWTle`), i dopiero
 *    gdy strona już się pokazała: po zdarzeniu `load`, potem po pobraniu zdjęć LCP
 *    (`img[fetchpriority="high"]`, pierwsze zdjęcia pierwszych kart) albo 3 s po `load`,
 *    co nastąpi pierwsze, a na końcu w bezczynności przeglądarki. Safari nie ma
 *    `navigator.connection`, tam tylko zamiar.
 *
 * DLACZEGO TAK PÓŹNO (Lighthouse Piotra 1.10, perf 59, LCP 8,0 s na telefonie). Sama
 * bezczynność przychodziła już ok. 650 ms po wejściu, w trakcie pobierania zdjęć z kart:
 * maplibre-gl (276 KB, w 84% nieużyte na liście), worker, CSS i MapView szły RAZEM ze
 * zdjęciem LCP i odbierały mu łącze. Mapa w tle ma przygotować wejście na mapę, a nie
 * spowalniać pierwszy ekran listy.
 *
 * Te same specyfikatory co w `SearchPage` (lazy MapView) i `MapaGlKontener` (maplibre, worker,
 * CSS): moduł ładuje się raz, więc późniejszy import dostaje gotowy kawałek z pamięci.
 */

type Ladowanie = () => Promise<unknown>;

const domyslneLadowanie: Ladowanie = () =>
  Promise.all([
    import('@/components/search/MapView'),
    import('maplibre-gl'),
    import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'),
    import('maplibre-gl/dist/maplibre-gl.css'),
  ]);

let obietnica: Promise<unknown> | null = null;

/** Raz na kartę; po błędzie (np. brak sieci) następny zamiar próbuje od nowa. */
export function dociagnijMape(ladowanie: Ladowanie = domyslneLadowanie): Promise<unknown> {
  if (!obietnica) {
    obietnica = ladowanie().catch(() => {
      obietnica = null;
    });
  }
  return obietnica;
}

/** Tylko dla testów: zapomina, że kawałki już dociągnięto. */
export function zapomnijDociagniecie(): void {
  obietnica = null;
}

export interface PolaczenieSieci {
  saveData?: boolean;
  effectiveType?: string;
}

/** Tło tylko na szybkim łączu i bez oszczędzania danych. Brak informacji = nie. */
export function moznaDociagacWTle(polaczenie: PolaczenieSieci | null | undefined): boolean {
  return !!polaczenie && polaczenie.saveData !== true && polaczenie.effectiveType === '4g';
}

/** Ile po `load` czekamy najdłużej na zdjęcia LCP, zanim dociągniemy mapę mimo to. */
export const MAKS_CZEKANIA_NA_LCP_MS = 3000;

type OknoBezczynnosci = Window & {
  requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/**
 * Woła `gotowe`, gdy wszystkie zdjęcia LCP strony są pobrane (albo nie dały się pobrać: błąd
 * też kończy czekanie). Bez takich zdjęć nie woła nic; wtedy decyduje limit czasu.
 * Zwraca sprzątanie słuchaczy.
 */
export function poZdjeciachLcp(dokument: Document, gotowe: () => void): () => void {
  const zdjecia = Array.from(dokument.querySelectorAll<HTMLImageElement>('img[fetchpriority="high"]'));
  if (zdjecia.length === 0) return () => {};
  const sprawdz = () => {
    if (zdjecia.every((z) => z.complete)) gotowe();
  };
  for (const z of zdjecia) {
    z.addEventListener('load', sprawdz);
    z.addEventListener('error', sprawdz);
  }
  sprawdz();
  return () => {
    for (const z of zdjecia) {
      z.removeEventListener('load', sprawdz);
      z.removeEventListener('error', sprawdz);
    }
  };
}

/**
 * Dociągnięcie w tle, jeśli łącze pozwala, dopiero po pierwszym ekranie (kolejność w nagłówku
 * pliku). Zwraca funkcję odwołującą (efekt Reacta) — sprząta każdy etap, na którym stoi.
 */
export function dociagnijWBezczynnosci(
  polaczenie: PolaczenieSieci | null | undefined = (navigator as Navigator & { connection?: PolaczenieSieci }).connection,
  ladowanie: Ladowanie = domyslneLadowanie,
  okno: OknoBezczynnosci = window,
): () => void {
  if (!moznaDociagacWTle(polaczenie)) return () => {};
  const dokument = okno.document;
  const sprzatanie: Array<() => void> = [];
  let odwolano = false;
  let ruszylo = false;

  // Etap 3: bezczynność przeglądarki. Raz, choć woła go i zdjęcie LCP, i limit czasu.
  const wBezczynnosci = () => {
    if (odwolano || ruszylo) return;
    ruszylo = true;
    if (okno.requestIdleCallback) {
      const id = okno.requestIdleCallback(() => void dociagnijMape(ladowanie), { timeout: 4000 });
      sprzatanie.push(() => okno.cancelIdleCallback?.(id));
    } else {
      const t = okno.setTimeout(() => void dociagnijMape(ladowanie), 1);
      sprzatanie.push(() => okno.clearTimeout(t));
    }
  };

  // Etap 2: zdjęcia LCP pobrane albo limit czasu po `load`.
  const poZaladowaniu = () => {
    if (odwolano) return;
    const t = okno.setTimeout(wBezczynnosci, MAKS_CZEKANIA_NA_LCP_MS);
    sprzatanie.push(() => okno.clearTimeout(t));
    sprzatanie.push(poZdjeciachLcp(dokument, wBezczynnosci));
  };

  // Etap 1: zdarzenie `load` (albo już po nim).
  if (dokument.readyState === 'complete') {
    poZaladowaniu();
  } else {
    okno.addEventListener('load', poZaladowaniu, { once: true });
    sprzatanie.push(() => okno.removeEventListener('load', poZaladowaniu));
  }

  return () => {
    odwolano = true;
    for (const f of sprzatanie) f();
  };
}
